import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "Components/UI/Paper";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ChatIcon from "@mui/icons-material/Chat";

import { getAllAgreementTemplates } from "API/swesum";
import { ClientForm } from "Components/Client/ClientForm";
import { BOOLVALUE } from "utils/options/BOOLVALUE";
import { COUNTRYCODE } from "utils/options/COUNTRYCODE";
import { LANGUAGECODE } from "utils/options/LANGUAGECODE";
import { DEFAULT_SERVICE } from "utils/options/DEFAULT_SERVICE";
import RefreshBtn from "Components/UI/Tables/RefreshBtn";
import { useClient } from "Context/Client";
import { CircularProgress, IconButton } from "@mui/material";
import { ClientFormData } from "utils/forms/ClientForm";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import ClientSearch from "Components/Client/ClientSearch";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useClients } from "Context/Clients";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";

const MoveDirection = {
  FORWARD: "forward",
  BACKWARD: "backward",
};

export function ClientCard({ showWarningLog, onClickWarningLog }) {
  const { client, loading, update } = useClient();
  const { clientShortInfo, fetchClientShortInfo } = useClients();

  const boolOptions = BOOLVALUE;
  const countryCodes = COUNTRYCODE;
  const languageCodes = LANGUAGECODE;
  const services = DEFAULT_SERVICE;

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [formDataCopy, setFormDataCopy] = useState(null);

  const [agreementOptions, setAgreementOptions] = useState(null);

  const navigate = useNavigate();
  const { openError, openSuccess, openInfo } = usePopupNotificationContext();

  const asyncFetchAgreementOptions = async () => {
    const data = await getAllAgreementTemplates();
    setAgreementOptions(data);
  };

  const handleInputChange = useCallback(
    (field, newVal) => {
      setFormData({ ...formData, [field]: newVal });
    },
    [formData]
  );

  const handleUpdate = async () => {
    const res = await update(formData);
    if (res.status === 201) {
      openSuccess("Kunden har sparats", 5000);
    } else {
      openError("Ett fel har inträffats", 5000);
    }
    setFormData(ClientFormData(res.data));
    setEditMode(false);
  };

  const handleReplaceFormData = (newFormData) => {
    setFormData(newFormData);
  };

  useEffect(() => {
    asyncFetchAgreementOptions();
    fetchClientShortInfo();
  }, []);

  useEffect(() => {
    if (client) {
      setFormData(ClientFormData(client));
    }
  }, [client]);

  const changeClient = (direction) => {
    if (!direction || !client) {
      return;
    }

    const sortedList = clientShortInfo.sort((c1, c2) =>
      c1.Title.localeCompare(c2.Title)
    );
    const currentIndex = sortedList.findIndex((c1) => c1.Id === client.Id);

    if (direction === MoveDirection.FORWARD) {
      if (currentIndex < sortedList.length - 1) {
        navigate(`/clients/${sortedList[currentIndex + 1].Id}`);
      } else {
        openInfo("Är på sista klienten i listan");
      }
    } else if (direction === MoveDirection.BACKWARD) {
      if (currentIndex > 0) {
        navigate(`/clients/${sortedList[currentIndex - 1].Id}`);
      } else {
        openInfo("Är på första klienten i listan", 3000);
      }
    }
  };

  return (
    <>
      <Paper
        sx={{ minHeight: "775px", display: "flex", flexDirection: "column" }}
      >
        {client ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <Typography
                  sx={{ marginBottom: "1.0rem" }}
                  variant="h5"
                  align="left"
                >
                  {client.Title}
                </Typography>
                {showWarningLog ? (
                  <ChatIcon
                    sx={{
                      color: "orange",
                      cursor: "pointer",
                    }}
                    onClick={() => onClickWarningLog()}
                  />
                ) : null}
              </div>

              <Box
                sx={{
                  flex: 1,
                  marginX: "20px",
                  maxWidth: "500px",
                  display: "flex",
                  gap: "15px",
                }}
              >
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={() => changeClient(MoveDirection.BACKWARD)}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={() => changeClient(MoveDirection.FORWARD)}
                >
                  <KeyboardArrowLeftIcon sx={{ rotate: "180deg" }} />
                </IconButton>
                <ClientSearch loading={loading} />
                <RefreshBtn />
              </Box>
            </Box>

            {formData && (
              <ClientForm
                formData={formData}
                FORM_MODE={editMode ? "EDIT" : "LOCKED"}
                handleInputChange={handleInputChange}
                handleReplaceFormData={handleReplaceFormData}
                countryCodes={countryCodes}
                languageCodes={languageCodes}
                services={services}
                agreementOptions={agreementOptions}
                boolOptions={boolOptions}
                client={client}
                loading={loading}
              />
            )}

            <Grid
              item
              xs={12}
              sx={{
                marginTop: "auto",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {editMode ? (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      setFormData(formDataCopy);
                      setEditMode(false);
                    }}
                    sx={{ display: editMode ? "flex" : "none" }}
                    startIcon={<CloseIcon />}
                  >
                    Kasta Ändringar
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleUpdate(formData)}
                    sx={{ ml: 2 }}
                    variant="contained"
                    startIcon={<CheckIcon />}
                  >
                    Spara Ändringar
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  onClick={() => {
                    setFormDataCopy(formData);
                    setEditMode(true);
                  }}
                  variant="contained"
                  sx={{
                    display: editMode ? "none" : "flex",
                  }}
                  startIcon={<EditIcon />}
                >
                  Editera
                </Button>
              )}
            </Grid>
          </>
        ) : loading ? (
          <CircularProgress />
        ) : null}
      </Paper>
    </>
  );
}
